import { CHECK_TYPE, CheckResultGeneric, GENERIC_RESULT_STATE } from '@solid/shared';
import classNames from 'classnames';
import React from 'react';
import { Helmet } from 'react-helmet';
import sanitizeHTML from 'sanitize-html';
import { ReactComponent as ResultIconInfo } from '../../assets/images/icons/checkup-info-filled.svg';
import { ReactComponent as ResultIconProhibition } from '../../assets/images/icons/checkup-prohibition-filled.svg';
import { ReactComponent as ResultIconSuccess } from '../../assets/images/icons/checkup-success-filled.svg';
import { ReactComponent as ResultIconFailed } from '../../assets/images/icons/checkup-warning-filled.svg';
import { ReactComponent as IconQuestion } from '../../assets/images/icons/question.svg';
import { ReactComponent as Logo } from '../../assets/images/logo.svg';
import { CheckResultResponse, isCheckResultGeneric, parseMarkdown } from '../../services/check.service';
import styles from './CheckReport.module.scss';
import { Trans, useTranslation } from 'react-i18next';
import * as Styled from './CheckEntry.styles';

const resultIconMap: Record<GENERIC_RESULT_STATE, React.FC<unknown> | null> = {
  [GENERIC_RESULT_STATE.SUCCESS]: ResultIconSuccess,
  [GENERIC_RESULT_STATE.FAILED]: ResultIconFailed,
  [GENERIC_RESULT_STATE.INFO]: ResultIconInfo,
  [GENERIC_RESULT_STATE.PROHIBITION]: ResultIconProhibition,
  [GENERIC_RESULT_STATE.NEUTRAL]: null,
};

interface CheckReportProps {
  check: CheckResultResponse;
  context?: string;
}

const CheckReport: React.FC<CheckReportProps> = ({ check, context }) => {
  let resultText: string = null;
  const { t } = useTranslation();
  if (isCheckResultGeneric(check.result)) {
    resultText = check.result.text;
  } else if (check.type !== CHECK_TYPE.DUAL_USE) {
    if (check.note) {
      resultText = parseMarkdown(check.note);
    } else {
      resultText = t('checks.checkReport.resultText');
    }
  }

  let ResultIcon: React.FC<any>;
  if (isCheckResultGeneric(check.result)) {
    const icon = resultIconMap[check.result.result];
    if (icon) {
      ResultIcon = icon;
    }
  }

  const isIndividualTest = check.type === CHECK_TYPE.INDIVIDUAL_TEST;
  let individualTestText = '';
  if (isIndividualTest) {
    const statusString: CheckResultGeneric = check.result as CheckResultGeneric;
    individualTestText = t('checks.individualTest.checkReport.' + statusString.result.toLowerCase());
  }
  return (
    <div className={styles.container}>
      <Helmet>
        <title>
          {t('checks.checkReport.pageTitle', {
            itemName: check?.itemName ?? '',
            submittedAt: check?.submittedAt?.toLocaleDateString() ?? '',
          })}
        </title>
      </Helmet>
      <div className={styles.header}>
        <Logo className={styles.headerLogo} />
        <p className={styles.headerHeadline}>{t('checks.checkReport.title')}</p>
      </div>
      <div className={styles.result}>
        <div className={styles.resultLeft}>
          <div className={styles.productName} dangerouslySetInnerHTML={{ __html: sanitizeHTML(check.itemName) }} />
          <div className={styles.date}>{check.submittedAt.toLocaleDateString()}</div>
          <Styled.UserName>
            <Trans i18nKey='checks.checkEntry.checkedBy' /> <b>{check.userName}</b>
          </Styled.UserName>
        </div>
        <div className={styles.resultRight}>
          <h2 className={styles.resultHeadline}>{t('checks.common.checkResultTitle')}</h2>
          {isIndividualTest && (
            <div className={styles.resultText} dangerouslySetInnerHTML={{ __html: sanitizeHTML(individualTestText) }} />
          )}
          <div className={styles.resultText} dangerouslySetInnerHTML={{ __html: sanitizeHTML(resultText) }} />
          {
            /**
             * Special case for "DUAL_USE".
             * Here the note is actually the result text, as there is no generic result text.
             * If the user has not provided a note, a warning will be displayed.
             */
            check.note && (
              <>
                {(check.type !== CHECK_TYPE.DUAL_USE || (check.type === CHECK_TYPE.DUAL_USE && resultText)) && (
                  <div className={classNames(styles.resultHeadline, styles.noteHeadline)}>
                    {t('checks.checkReport.comment')}
                  </div>
                )}
                <div
                  className={classNames(styles.resultText, styles.noteText)}
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(parseMarkdown(check.note)) }}
                />
              </>
            )
          }
        </div>
        <div className={styles.icon}>{ResultIcon && <ResultIcon className={styles.resultIcon} />}</div>
      </div>
      <table className={styles.questionsTable}>
        <thead>
          <tr>
            <th>{t('checks.checkReport.questions')}</th>
            <th>{t('checks.checkReport.answers')}</th>
          </tr>
        </thead>
        <tbody>
          {check.questions.map(({ question, value, label }) => (
            <tr key={question}>
              {isIndividualTest ? (
                <td>{t(`common.${label}`)}</td>
              ) : (
                <td dangerouslySetInnerHTML={{ __html: sanitizeHTML(parseMarkdown(question)) }}></td>
              )}
              <td
                dangerouslySetInnerHTML={{
                  __html: sanitizeHTML(value === true ? t('common.yes') : value === false ? t('common.no') : value),
                }}></td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className={styles.footer}>
        <IconQuestion className={styles.footerIcon} />
        <div className={styles.footerContainer}>
          <div className={styles.footerHeadline}>
            <Trans i18nKey='checks.checkReport.footerHeadline' components={{ br: <br /> }} />
          </div>
          <div className={styles.footerContactContainer}>
            <div>
              E-Mail: <a href='mailto:info@solid-app.de'>info@solid-app.de</a>
            </div>
            <div>
              AEB SE
              <br />
              Sigmaringer Strasse 109
              <br />
              D-70567 Stuttgart
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckReport;
